import { effect, Injectable, signal } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Data, Router } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { AuthPartialRegService } from "@core/AuthServices/authPartialReg/auth-partial-reg.service";
import { BehaviorSubject, distinctUntilChanged, Observable, Subscription, take } from 'rxjs';
import { CommonVariable } from '../app.constants';
import { InboxMessageComponent } from '../components/inbox-message/inbox-message.component';
import { InboxMsgFullComponent } from '../components/inbox-msg-full/inbox-msg-full.component';
import { NarratorTipsComponent } from '../components/narrator-tips/narrator-tips.component';
import { LuckyNosPopupComponent } from '../lucky-nos-popup/lucky-nos-popup.component';
import { NarratorService } from './narrator.service';
import { OrientationService } from './orientation.service';
import { NarratorTipService } from './AppRootService/narrator-tip.service';
const fiveThousand = 5000;

@Injectable({
  providedIn: "root",
})
export class NotificationManagerService {
   private notificationTrigger = signal<Data | null>(null);
   private showNotificationSub?: Subscription;
  constructor(
    private router: Router,
    private authService: AuthService,
    private orientationService: OrientationService,
    private authPartialRegService: AuthPartialRegService,
    private bottomSheet: MatBottomSheet,
    private matDialog: MatDialog,
    private narratorService: NarratorService,
    private narratorTipService : NarratorTipService
  ) {
    // Run showNotification automatically on every change
        effect(() => {
          const notificationData = this.notificationTrigger();
          if (!notificationData) return;
          queueMicrotask(() => this.showNotification());// decouple side effect
          this.notificationTrigger.set(null); // clear trigger after scheduling
        });
    this.checkAuthPartialRegDismissedEvents();
    this.authService?.selectedlanguage$?.subscribe({
      next: (l) => {
        this.selectedLanguage = l;
      },
      error: (err) => {
        return err;
      }
    });

    this.narratorTipDismissedSubject.subscribe((state) => {
      if (state) {
        this.dismissNotification();
      }
    })
  }

  private notificationQueue: Data[] = [];
  private currentNotificationSubject = new BehaviorSubject<Data | null>(null);
  currentNotification$: Observable<Data | null> = this.currentNotificationSubject.asObservable();
  private lastProcessedNotificationId: string | null = null;
  /** Prevents opening the same narrator tip twice (duplicate addNotification or BehaviorSubject replay on re-subscribe). */
  private activeNarratorTipDocumentId: string | null = null;
  private notificationInterval = 2000;
  selectedLanguage: string = CommonVariable.preferredLanguage;
  private narratorTipTrackerForChangeDetectionSubject = new BehaviorSubject<Data>({});
  narratorTipTrackerForChangeDetection$ = this.narratorTipTrackerForChangeDetectionSubject.asObservable();
  private narratorTipDismissedSubject = new BehaviorSubject<boolean>(false);
  private showMenuNarratorTip = this.authService.showMenuNarratorTip;
  // Method to add a new notification
  addNotification(notification: Data): void {
    const id = notification?.id;
    // Dedupe first: do not set notificationTrigger before these checks, or the effect re-runs
    // showNotification() and BehaviorSubject replay opens the same narrator tip twice (e.g. tabs + home ionViewWillEnter).
    if (id != null && this.lastProcessedNotificationId === id) {
      return;
    }
    if (id != null && this.currentNotificationSubject.value?.id === id) {
      return;
    }
    const existingIndex = id != null ? this.notificationQueue.findIndex((n) => n.id === id) : -1;
    if (existingIndex !== -1) {
      this.notificationQueue[existingIndex] = notification;
      this.reorganizeQueue();
      return;
    }

    if (id != null) {
      this.lastProcessedNotificationId = id;
    }
    this.notificationTrigger.set(notification);

    this.notificationQueue.push(notification);

    // Reorganize the queue based on the current page or priority
    this.reorganizeQueue();

    // If no notification is currently being shown, process the queue
    if (!this.currentNotificationSubject.value || (notification?.setting?.page_name === "menu" && notification?.urlPageName === "menu"  && this.showMenuNarratorTip()) || notification?.template_name === "default" 
) {
      this.processQueue();
    }

    setTimeout(() => {
      this.lastProcessedNotificationId = null;
    }, 2500);
  }

  // Method to reorganize the queue
  private reorganizeQueue(): void {
    const currentPage = this.getCurrentPage();
    const narratorTipCurrentUrl = this.narratorService?.getCurrentPageURL();
    const narratorTipCurrentPage = narratorTipCurrentUrl.includes("?") ? narratorTipCurrentUrl.split("?")[0] : narratorTipCurrentUrl;

    // Separate notifications based on type
    let autoShowInboxNotifications = this.notificationQueue.filter(n => n.type === "auto_show_inbox");
    let narratorTipNotifications = this.notificationQueue.filter(n => n.type === "narrator_tip");
    let otherNotifications = this.notificationQueue.filter(n => n.type !== "auto_show_inbox" && n.type !== "narrator_tip");

    // Sort auto_show_inbox notifications by expiry_date
    autoShowInboxNotifications.sort((a, b) => {
      // Handle both Firestore Timestamp (has .toDate()) and plain Date objects
      const expiryA = a.expiry_date?.toDate 
        ? a.expiry_date.toDate().getTime() 
        : a.expiry_date instanceof Date 
          ? a.expiry_date.getTime() 
          : typeof a.expiry_date === 'number' 
            ? a.expiry_date 
            : 0;
      const expiryB = b.expiry_date?.toDate 
        ? b.expiry_date.toDate().getTime() 
        : b.expiry_date instanceof Date 
          ? b.expiry_date.getTime() 
          : typeof b.expiry_date === 'number' 
            ? b.expiry_date 
            : 0;
      return expiryA - expiryB;
    });
    
    // Sort narrator_tip notifications by creation date
    narratorTipNotifications.sort((a, b) => {
      // Handle both Firestore Timestamp (has .toDate()) and plain Date objects
      const creationA = a?.setting?.creation_timestamp?.toDate 
        ? a.setting.creation_timestamp.toDate().getTime() 
        : a?.setting?.creation_timestamp instanceof Date 
          ? a.setting.creation_timestamp.getTime() 
          : typeof a?.setting?.creation_timestamp === 'number' 
            ? a.setting.creation_timestamp 
            : a.creation_date ?? 0;
      const creationB = b?.setting?.creation_timestamp?.toDate 
        ? b.setting.creation_timestamp.toDate().getTime() 
        : b?.setting?.creation_timestamp instanceof Date 
          ? b.setting.creation_timestamp.getTime() 
          : typeof b?.setting?.creation_timestamp === 'number' 
            ? b.setting.creation_timestamp 
            : b.creation_date ?? 0;
      return creationA - creationB;
    });

    // Reorganize auto_show_inbox notifications
    const sortedAutoShowInbox = [
      ...autoShowInboxNotifications.filter(n => n.auto_show_inbox_page === currentPage),
      ...autoShowInboxNotifications.filter(n => n.auto_show_inbox_page === "all"),
      ...autoShowInboxNotifications.filter(n => n.auto_show_inbox_page !== currentPage && n.auto_show_inbox_page !== "all"),
    ];

    // Reorganize narrator_tip notifications
    const sortedNarratorTip = [
      ...narratorTipNotifications.filter(n => n.setting?.page_name === narratorTipCurrentPage),
      ...narratorTipNotifications.filter(n => n.setting?.page_name === "all"),
      ...narratorTipNotifications.filter(n => n.setting?.page_name !== narratorTipCurrentPage && n.setting?.page_name !== "all"),
    ];

    // Rebuild the queue with sorted notifications, maintaining order
    this.notificationQueue = [...sortedAutoShowInbox, ...sortedNarratorTip, ...otherNotifications];
  }

  // Method to process the queue
  private processQueue(): void {
    if (this.notificationQueue.length > 0) {
      if (this.isBlockingPopupOpen()) {
        // Wait for 5s and retry when popups are dismissed
        setTimeout(() => this.processQueue(), fiveThousand);
        return;
      }

      // Take the first notification and emit it
      const nextNotification = this.notificationQueue.shift();

      if (nextNotification) {
        // Remove duplicates based on the ID of the emitted notification
        this.notificationQueue = this.notificationQueue.filter(
          (notification) => notification.id !== nextNotification.id
        );
        // Emit the next notification
        this.currentNotificationSubject.next(nextNotification);
      }
    }
  }

  // Method to dismiss the current notification
  dismissNotification(): void {
    setTimeout(() => {
      this.currentNotificationSubject.next(null);
      this.activeNarratorTipDocumentId = null;
      this.reorganizeQueue();
      this.processQueue();
    }, this.notificationInterval);
  }

  // Utility to get the current page (replace with actual logic)
  private getCurrentPage(): string {
    const urlSegments = this.router.url.split("?")[0].split("/"); // Ignore query params
    const currentPage = urlSegments[urlSegments.length - 1];
    return currentPage;
  }

  openMessage(message: Data) {
    if (!message) return;
    if (this.authService.templateNameArray.includes(message?.template_name)) {
      this.authPartialRegService.openAutoShowInbox(message);
    } else if (message && message?.message_type == "full_screen_promo_msg") {
      // this.router.navigate(["/inbox-msg-full"]);
      const matDialogRef = this.matDialog.open(InboxMsgFullComponent, {
        data: message,
        panelClass: "inbox-message-container",
      });

      matDialogRef.afterClosed().subscribe(() => {
        this.dismissNotification();
      });
      return;
    } else if (message && message?.message_type == "lucky_number_popup_msg") {
      this.openLuckyPopup(message);
    } else {
      const panelClass = this.orientationService.isFullscreen()
        ? "inbox-bottom-sheet-fullscreen"
        : message?.template_type === "plunko_christmas" || message?.template_type === "valentine_plunko_campaign"
        ? "bottom-sheet-fullscreen"
        : message?.template_type === "inbox_churn_risk"
        ? this.selectedLanguage === "es"
          ? "churn-inbox-bottomsheet-es"
          : this.selectedLanguage === "fr"
          ? "churn-inbox-bottomsheet-fr"
          : "churn-inbox-bottomsheet"
        : "inbox-bottom-sheet";
      const bottomSheetRef = this.bottomSheet.open(InboxMessageComponent, {
        panelClass: panelClass,
        data: message,
      });

      bottomSheetRef
        .afterDismissed()
        .pipe(take(1))
        .subscribe(() => {
          this.dismissNotification();
        });
    }
  }

  showNotification() {
    // Tear down any existing subscription before creating a new one
    this.showNotificationSub?.unsubscribe();

    this.showNotificationSub = this.currentNotification$.subscribe((notification) => {
      if (!notification) return;
      const currentPage = this.getCurrentPage();
      if (notification.type === "auto_show_inbox" && (notification.auto_show_inbox_page === currentPage || notification.auto_show_inbox_page === "all")) {
        this.openMessage(notification);
      } else if (notification.type === "narrator_tip") {
        this.openNarratorTip(notification);
      }
    });
  }

  openLuckyPopup(message) {
    try {
      if (this.matDialog.openDialogs?.length) return;
      const modal = this.matDialog.open(LuckyNosPopupComponent, {
        panelClass: "lucky-number-popup",
        minHeight: "60vh",
        width: "85vw",
        data: message,
        hasBackdrop: true,
      });
      modal.backdropClick()?.subscribe(() => {
        modal.close();
        this.dismissNotification();
      });
    } catch (e) {
      return e;
    }
  }

  checkAuthPartialRegDismissedEvents() {
    this.authPartialRegService?.autoShowInboxSubject.pipe(distinctUntilChanged()).subscribe((event) => {
      if (event) {
        this.dismissNotification();
        this.authPartialRegService?.autoShowInboxSubject.next(false);
      }
    });
  }

  // To open the narrator tip
  private openNarratorTip(narratorData: Data) {
    console.info("Attempting to open narrator tip:", narratorData);
    // Check if a popup/bottomsheet is open in view to delay the notification
     if(this.isBlockingPopupOpen()) return;
    const docId = narratorData?.id;
    if (docId && this.activeNarratorTipDocumentId === docId) {
      return;
    }
    const dialogRef = this.matDialog.open(NarratorTipsComponent, {
      panelClass: "narrator-tip",
      height: "100%",
      width: "100%",
      data: narratorData,
    });
    this.activeNarratorTipDocumentId = docId ?? null;
    dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (this.activeNarratorTipDocumentId === docId) {
        this.activeNarratorTipDocumentId = null;
      }
    });
    this.narratorTipTrackerForChangeDetectionSubject.next({page: narratorData?.setting?.page_name});
  }

  public setNarratorTipDismissedState(state: boolean) {
    this.narratorTipDismissedSubject.next(state); // Emit the state;
  }
  
  // Method to check if a popup/bottomsheet is open in view to delay the notification in queue
  private isBlockingPopupOpen(): boolean {
    try {
      const isDialogOpen = this.matDialog.openDialogs.length > 0;
      const isBottomSheetOpen = this.bottomSheet._openedBottomSheetRef !== null;

      const promoPopupElement = document.querySelector('.CT_InterstitialWrapper');
      const isPromoPopupOpen = !!promoPopupElement; // Check for existence, not just null

      return isDialogOpen || isBottomSheetOpen || isPromoPopupOpen;

    } catch (error) {
      this.authService.errorHandlerLogs("Error checking blocking popup:", error);
      return true; // Return true to prevent the queue from processing
    }
  }
}
